import { useEffect } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import { ArrowUpRight, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import FounderParticles from "@/components/site/FounderParticles";
import WordsRise from "@/components/site/WordsRise";
import { usePrefersReducedMotion } from "@/hooks/usePrefersReducedMotion";
import { OPERATORS } from "@/data/operators";

const EASE = [0.16, 1, 0.3, 1];

const PRINCIPLES = [
  { n: "01", title: "Focus on who you work with.", body: "The cost of a wrong hire compounds. Better to wait for the person who raises the bar than to fill a seat and spend a year undoing it." },
  { n: "02", title: "Culture travels through people, not documents.", body: "No handbook sets the tone. The first ten people do \u2014 and everyone after them learns the standard by watching how those ten actually work." },
  { n: "03", title: "Deep, not wide.", body: "Solve one real problem completely before reaching for the next. Focus is the rarest advantage a small team has, and the easiest to give away." },
  { n: "04", title: "Ask for the decision.", body: "Meetings that end without a decision are just expensive updates. Name the choice, make it, and move \u2014 momentum is a feature, not an accident." },
  { n: "05", title: "Build AI-native from day one.", body: "AI isn\u2019t a layer you add later. The companies worth building now assume it in the foundation, from the data model up." },
];

/* ---------- Scroll progress bar (this page only) ---------- */
function ProgressBar() {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 30, mass: 0.3 });
  return (
    <motion.div
      data-testid="founder-progress-bar"
      style={{ scaleX }}
      className="fixed inset-x-0 top-0 z-[60] h-[3px] origin-left bg-gold"
    />
  );
}

/* ---------- Prose paragraph that fades up ---------- */
function Para({ children, className = "" }) {
  return (
    <motion.p
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-12%" }}
      transition={{ duration: 0.8, ease: EASE }}
      className={className}
    >
      {children}
    </motion.p>
  );
}

/* ---------- How he operates block ---------- */
function Principle({ p, index }) {
  const right = index % 2 === 1;
  return (
    <motion.div
      data-testid={`principle-${p.n}`}
      initial={{ opacity: 0, x: right ? 60 : -60 }}
      whileInView={{ opacity: 1, x: 0 }}
      viewport={{ once: true, margin: "-15%" }}
      transition={{ duration: 0.8, ease: EASE }}
      className={`relative max-w-[55ch] ${right ? "ml-auto text-right" : "mr-auto text-left"}`}
    >
      <span
        aria-hidden
        className={`pointer-events-none absolute -top-16 font-serif leading-none text-gold/[0.06] ${right ? "right-0" : "left-0"}`}
        style={{ fontSize: "clamp(7rem, 14vw, 12rem)" }}
      >
        {p.n}
      </span>
      <h3 className="relative font-serif text-4xl leading-[1.03] tracking-tight text-cream md:text-5xl">
        {p.title}
      </h3>
      <p className={`relative mt-5 text-[1.15rem] leading-[1.75] text-mute ${right ? "ml-auto" : "mr-auto"}`}>
        {p.body}
      </p>
    </motion.div>
  );
}

export default function FounderStory() {
  const reduced = usePrefersReducedMotion();

  useEffect(() => {
    const prevTitle = document.title;
    document.title = "Rab Govil — Founder, Govil Ventures";
    let meta = document.querySelector('meta[name="description"]');
    let created = false;
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
      created = true;
    }
    const prevDesc = meta.getAttribute("content");
    meta.setAttribute(
      "content",
      "My purpose is to help my teams win. Rab Govil has spent twenty-five years building companies — and discovering the work he cares about most is the people doing it."
    );
    return () => {
      document.title = prevTitle;
      if (created) meta.remove();
      else if (prevDesc !== null) meta.setAttribute("content", prevDesc);
    };
  }, []);

  return (
    <div data-testid="founder-page" className="bg-noir text-cream">
      <ProgressBar />

      {/* ---------- PAGE HERO ---------- */}
      <section className="relative flex min-h-[88vh] items-center overflow-hidden">
        {!reduced && (
          <div className="absolute inset-0 z-0 opacity-40">
            <FounderParticles />
          </div>
        )}
        <div className="pointer-events-none absolute inset-0 z-[1] bg-gradient-to-b from-noir/50 via-transparent to-noir" />
        <div className="relative z-10 mx-auto grid w-full max-w-7xl grid-cols-1 items-center gap-12 px-6 pt-32 pb-20 md:px-12 lg:grid-cols-12 lg:gap-10 lg:pb-24">
          {/* LEFT — text (7 cols) */}
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.15 }}
              className="mb-7 text-[0.72rem] font-semibold uppercase tracking-[0.34em] text-gold"
            >
              The Person Behind the Firm
            </motion.p>
            <WordsRise
              as="h1"
              testid="founder-hero-heading"
              text="My purpose is to help my teams win."
              className="max-w-[16ch] font-serif font-bold leading-[0.98] tracking-tight text-cream"
              style={{ fontSize: "clamp(2.6rem, 5.6vw, 5rem)", fontWeight: 700 }}
            />
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: EASE, delay: 0.9 }}
              className="mt-8 max-w-xl text-[1.1rem] leading-relaxed text-mute"
            >
              Rab Govil has spent twenty-five years building companies, and the same
              amount of time discovering that the work he cares about most isn&rsquo;t
              the building &mdash; it&rsquo;s the people doing it.
            </motion.p>
          </div>

          {/* RIGHT — portrait (5 cols) */}
          <div className="lg:col-span-5">
            <motion.figure
              data-testid="founder-hero-portrait-figure"
              className="relative mx-auto w-full max-w-[380px] lg:mx-0 lg:ml-auto"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, ease: EASE, delay: 0.5 }}
            >
              <div className="relative overflow-hidden rounded-[4px]" style={{ aspectRatio: "4 / 5" }}>
                <motion.img
                  data-testid="founder-hero-portrait"
                  src="/rab.jpg"
                  alt="Rab Govil, Founder of Govil Ventures"
                  decoding="async"
                  className="h-full w-full object-cover object-top"
                  style={{ scale: 1.04 }}
                  initial={{ filter: "grayscale(100%)", opacity: 0.7 }}
                  animate={{ filter: "grayscale(0%)", opacity: 1 }}
                  transition={{ duration: 1.1, ease: EASE, delay: 0.7 }}
                />
                {/* subtle warm grade */}
                <div className="pointer-events-none absolute inset-0 [background:linear-gradient(160deg,rgba(201,162,39,0.10)_0%,transparent_45%,rgba(10,10,10,0.30)_100%)]" />
                {/* gold hairline tracing bottom + left, drawing in */}
                <motion.span
                  aria-hidden
                  className="absolute bottom-0 left-0 h-px w-full origin-left bg-gold"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 1 }}
                />
                <motion.span
                  aria-hidden
                  className="absolute bottom-0 left-0 h-full w-px origin-bottom bg-gold"
                  initial={{ scaleY: 0 }}
                  animate={{ scaleY: 1 }}
                  transition={{ duration: 0.9, ease: EASE, delay: 1.1 }}
                />
              </div>
              <figcaption className="mt-4 text-[0.68rem] uppercase tracking-[0.28em] text-gold">
                Rab Govil &mdash; Founder
              </figcaption>
            </motion.figure>
          </div>
        </div>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.6, duration: 1 }}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-mute"
        >
          <ChevronDown className="h-6 w-6 animate-bounce" />
        </motion.div>
      </section>

      {/* ---------- THE PATTERN ---------- */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-[68ch]">
          <div className="space-y-7 text-[1.25rem] leading-[1.75] text-cream/80">
            <Para>
              Rab has spent twenty-five years believing something that sounds soft
              until you watch him operate on it: companies don&rsquo;t win, teams do.
              He founded Nimblefish and led it to acquisition by RR Donnelley. He
              built Naehas with almost no outside capital into a platform some of the
              largest financial institutions in the country now depend on. Different
              decades, different markets, different products &mdash; and the same
              actual job every time. Get the right people pointed at a hard problem,
              then remove whatever is standing between them and winning.
            </Para>
            <Para>
              Along the way something became obvious. The part of the job he kept
              returning to wasn&rsquo;t the product roadmap or the board deck. It was
              sitting with a team that was stuck, and finding the way through with
              them.
            </Para>
            <Para className="pt-4 font-quote text-[1.9rem] italic leading-[1.35] text-cream md:text-[2.4rem]">
              That&rsquo;s not a side effect of the work. For Rab, it is the work.
            </Para>
          </div>
        </div>
      </section>

      {/* ---------- WHAT BOOTSTRAPPING TAUGHT HIM ---------- */}
      <section className="border-t border-white/10 px-6 py-24 md:py-32">
        <div className="mx-auto max-w-[68ch]">
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, ease: EASE }}
            className="mb-10 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
          >
            What Bootstrapping Taught Him
          </motion.p>
          <div className="space-y-7 text-[1.25rem] leading-[1.75] text-cream/80">
            <Para>
              Building Naehas without a venture safety net changed how Rab sees
              company-building. Every hire, every dollar, every month of runway had
              to earn its place. There was no round to paper over a wrong turn
              &mdash; only the discipline of making the next decision count.
            </Para>
            <Para>
              Hiring the first engineers, standing up finance and legal, wiring
              together the infrastructure a company needs just to exist &mdash; none
              of it was the interesting part, and all of it was slow, expensive, and
              nearly identical from one company to the next.
            </Para>
            <Para>
              Meanwhile, the two things that genuinely can&rsquo;t be manufactured
              &mdash; hard-won judgment and the relationships that open doors &mdash;
              were exactly what an experienced operator already had in abundance, and
              exactly what a first-time founder spends years trying to acquire.
            </Para>
          </div>
        </div>
      </section>

      {/* ---------- HOW HE OPERATES ---------- */}
      <section className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32">
        <div className="mx-auto max-w-6xl">
          <h2 className="mb-24 font-serif text-4xl leading-[1.02] tracking-tight text-cream md:text-6xl">
            How we operate.
          </h2>
          <div className="flex flex-col gap-28 md:gap-36">
            {PRINCIPLES.map((p, i) => (
              <Principle key={p.n} p={p} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* ---------- WHEN THERE'S NOTHING IN IT FOR HIM ---------- */}
      <section
        data-testid="giving-section"
        className="border-t border-white/10 px-6 py-24 md:py-32"
        style={{ backgroundColor: "#0E0E15" }}
      >
        <div className="mx-auto max-w-[68ch]">
          <h2 className="mb-10 font-serif text-4xl leading-[1.03] tracking-tight text-cream md:text-5xl">
            When there&rsquo;s nothing in it for him.
          </h2>
          <div className="space-y-7 text-[1.25rem] leading-[1.75] text-cream/80">
            <Para>
              Some of the work Rab cares most about pays nothing at all. As a founding
              board member of AI4ALL, he helped turn a Stanford summer program into a
              national nonprofit widening who gets to build artificial intelligence.
            </Para>
            <Para>
              He has served the digital-print industry consortium PODi, and holds an
              MS in Computational Imaging from the Rochester Institute of Technology,
              where he was a President&rsquo;s Scholar.
            </Para>
            <Para>
              The through-line isn&rsquo;t strategy. It&rsquo;s the same instinct that
              shows up everywhere else &mdash; find a group of people trying to do
              something hard, and help them win.
            </Para>
          </div>
          <div className="mt-10 text-[0.72rem] font-semibold uppercase tracking-[0.28em] text-mute">
            AI4ALL &middot; PODi &middot; RIT
          </div>
        </div>
      </section>

      {/* ---------- THE OPERATORS ---------- */}
      <section
        data-testid="founder-operators-section"
        className="border-t border-white/10 px-6 py-24 md:px-12 md:py-32"
      >
        <div className="mx-auto max-w-6xl">
          <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
            <div>
              <motion.p
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, ease: EASE }}
                className="mb-4 text-[0.72rem] font-semibold uppercase tracking-[0.32em] text-gold"
              >
                The Operators
              </motion.p>
              <h2 className="max-w-2xl font-serif text-4xl leading-[1.03] tracking-tight text-cream md:text-5xl">
                The people he&rsquo;s building with.
              </h2>
            </div>
            <Link
              to="/operators"
              data-testid="founder-operators-viewall"
              className="group inline-flex items-center gap-2 text-[0.72rem] font-semibold uppercase tracking-[0.2em] text-cream"
            >
              <span className="relative">
                Meet all operators
                <span className="absolute -bottom-1.5 left-0 h-px w-0 bg-gold transition-all duration-300 group-hover:w-full" />
              </span>
              <ArrowUpRight className="h-4 w-4 text-gold transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </div>

          <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-3">
            {OPERATORS.map((op, i) => {
              const CardTag = op.detail ? Link : op.linkedin ? "a" : "div";
              const cardProps = op.detail
                ? { to: `/operators/${op.slug}` }
                : op.linkedin
                ? { href: op.linkedin, target: "_blank", rel: "noopener noreferrer" }
                : {};
              return (
              <motion.div
                key={op.slug}
                initial={{ opacity: 0, y: 26 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-8%" }}
                transition={{ duration: 0.7, ease: EASE, delay: i * 0.08 }}
              >
                <CardTag
                  {...cardProps}
                  data-testid={`founder-operator-${op.slug}`}
                  className="group block"
                >
                  <div className="relative overflow-hidden rounded-[4px] border border-white/10" style={{ aspectRatio: "4 / 5" }}>
                    {op.image ? (
                      <img
                        src={op.image}
                        alt={`${op.name}, ${op.role} of ${op.company}`}
                        loading="lazy"
                        decoding="async"
                        className="h-full w-full object-cover object-top grayscale transition-all duration-700 group-hover:scale-[1.03] group-hover:grayscale-0"
                      />
                    ) : (
                      <div className="flex h-full w-full items-center justify-center bg-[linear-gradient(155deg,#171717_0%,#0d0d0d_100%)]">
                        <span className="font-serif text-5xl font-bold text-gold/25">
                          {op.name.split(" ").map((p) => p[0]).join("").slice(0, 2)}
                        </span>
                      </div>
                    )}
                    <span className="pointer-events-none absolute bottom-3 left-4 text-[0.58rem] font-semibold uppercase tracking-[0.22em] text-gold">
                      {op.company}
                    </span>
                  </div>
                  <h3 className="mt-4 font-serif text-2xl leading-none tracking-tight text-cream">
                    {op.name}
                  </h3>
                  <p className="mt-1.5 text-[0.68rem] font-medium uppercase tracking-[0.18em] text-mute">
                    {op.role} &middot; {op.company}
                  </p>
                </CardTag>
              </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ---------- CLOSING ---------- */}
      <section className="relative border-t border-white/10 px-6 py-32 text-center md:py-44">
        <span
          aria-hidden
          className="pointer-events-none absolute left-1/2 top-16 -translate-x-1/2 select-none font-serif leading-none text-gold/10"
          style={{ fontSize: "clamp(10rem, 22vw, 20rem)" }}
        >
          &ldquo;
        </span>
        <motion.blockquote
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-15%" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative mx-auto max-w-4xl font-serif leading-[1.04] tracking-tight text-cream"
          style={{ fontSize: "clamp(2.5rem, 6vw, 5rem)", fontWeight: 700 }}
        >
          Help your teams win. Everything else follows from that.
        </motion.blockquote>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.1 }}
          className="mx-auto mt-10 max-w-xl text-[1.05rem] leading-relaxed text-mute"
        >
          If you&rsquo;ve spent your career earning the kind of insight that
          can&rsquo;t be taught, he&rsquo;d like to see it.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
          className="mt-12"
        >
          <a
            data-testid="founder-linkedin-cta"
            href="https://www.linkedin.com/company/govilventures/"
            target="_blank"
            rel="noopener noreferrer"
            className="group inline-flex items-center gap-2.5 rounded-full bg-gold px-8 py-3.5 text-[0.72rem] font-semibold uppercase tracking-[0.18em] text-noir transition-transform duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(201,162,39,0.35)]"
          >
            Connect on LinkedIn
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
        </motion.div>
      </section>
    </div>
  );
}
